import { ROUTE_LABELS } from '@/app/router/routes'
import type { BreadcrumbOverride } from '@/shared/store/header-nav.store'

const APP_NAME = 'IDC Real Estate'
const HIDDEN_SEGMENTS = new Set(['admin', 'auth', 'c'])
const NON_NAVIGABLE_SEGMENTS = new Set(['individual', 'juridical', 'view', 'edit', 'create', 'new'])
const OBJECT_ID_PATTERN = /^[a-f0-9]{24}$/
const UUID_PATTERN = /^[a-f0-9-]{36}$/

type BreadcrumbSource = 'fallback' | 'override' | 'route'

export type BreadcrumbEntry = {
  documentTitle?: string
  href: string | null
  key: string
  label: string
  segment: string
  source: BreadcrumbSource
}

export type RouteContext = {
  breadcrumbs: BreadcrumbEntry[]
  documentTitle: string
}

function isLikelyId(segment: string) {
  return OBJECT_ID_PATTERN.test(segment) || UUID_PATTERN.test(segment)
}

export function buildBreadcrumbs(
  pathname: string,
  overrides: BreadcrumbOverride[]
): BreadcrumbEntry[] {
  const segments = pathname.split('/').filter(Boolean)
  const items: BreadcrumbEntry[] = []

  for (let index = 0; index < segments.length; index++) {
    const segment = segments[index]
    if (HIDDEN_SEGMENTS.has(segment)) continue

    const href = `/${segments.slice(0, index + 1).join('/')}`
    const override = overrides.find((item) => item.segment === segment)
    const routeLabel = ROUTE_LABELS[segment]
    const source: BreadcrumbSource = override ? 'override' : routeLabel ? 'route' : 'fallback'
    const label = override?.label ?? routeLabel ?? segment
    const isNavigable = !NON_NAVIGABLE_SEGMENTS.has(segment) && !isLikelyId(segment)

    items.push({
      documentTitle: override?.documentTitle,
      href: isNavigable ? href : null,
      key: `${segment}-${index}`,
      label,
      segment,
      source
    })
  }

  return items
}

export function buildDocumentTitle(breadcrumbs: BreadcrumbEntry[]) {
  const entity = breadcrumbs.findLast((item) => item.source === 'override')
  const mostSpecificRoute = breadcrumbs.findLast((item) => item.source === 'route')
  const pageTitle = entity?.documentTitle ?? entity?.label ?? mostSpecificRoute?.label

  return pageTitle ? `${pageTitle} | ${APP_NAME}` : APP_NAME
}

export function buildRouteContext(pathname: string, overrides: BreadcrumbOverride[]): RouteContext {
  const breadcrumbs = buildBreadcrumbs(pathname, overrides)

  return {
    breadcrumbs,
    documentTitle: buildDocumentTitle(breadcrumbs)
  }
}
